import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import {
  MapPin, Plus, Edit2, Trash2, CheckCircle, Package,
  CreditCard, ArrowLeft, Loader2, AlertCircle, Home as HomeIcon
} from 'lucide-react';
import { API_URL } from '../config/api';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cart, getCartTotal, clearCart } = useCart();
  const { user, session } = useAuth();
  
  const [step, setStep] = useState(1); // 1: Address, 2: Review, 3: Processing, 4: Success
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [razorpayKey, setRazorpayKey] = useState(null);
  const [orderNumber, setOrderNumber] = useState('');
  const [orderId, setOrderId] = useState('');
  
  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [showCoupons, setShowCoupons] = useState(false);
  
  // Address form state
  const [addressForm, setAddressForm] = useState({
    address_type: 'home',
    full_name: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    landmark: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India',
    is_default: false
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    if (cart.length === 0) {
      navigate('/cart');
      return;
    }
    
    fetchAddresses();
    fetchRazorpayKey();
    fetchAvailableCoupons();
  }, [user, cart]);
  
  const fetchAvailableCoupons = async () => {
    try {
      const response = await fetch(`${API_URL}/api/coupons/available/${user.id}`);
      const result = await response.json();
      if (result.success) {
        setAvailableCoupons(result.coupons || []);
      }
    } catch (err) {
      console.error('Error fetching coupons:', err);
    }
  };

  const fetchRazorpayKey = async () => {
    try {
      const response = await fetch(`${API_URL}/api/payments/config`);
      const result = await response.json();
      if (result.success) {
        setRazorpayKey(result.key);
      }
    } catch (err) {
      console.error('Error fetching Razorpay key:', err);
    }
  };

  const fetchAddresses = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/addresses?user_id=${user.id}`);
      const result = await response.json();
      
      if (result.success) {
        setAddresses(result.data);
        
        // Auto-select default or first address
        const defaultAddr = result.data.find(addr => addr.is_default);
        if (defaultAddr) {
          setSelectedAddress(defaultAddr);
        } else if (result.data.length > 0) {
          setSelectedAddress(result.data[0]);
        }
      }
    } catch (err) {
      console.error('Error fetching addresses:', err);
      setError('Failed to load addresses');
    } finally {
      setLoading(false);
    }
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/addresses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...addressForm,
          user_id: user.id
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        setAddresses([result.data, ...addresses]);
        setSelectedAddress(result.data);
        setShowAddressForm(false);
        
        // Reset form
        setAddressForm({
          address_type: 'home',
          full_name: '',
          phone: '',
          address_line1: '',
          address_line2: '',
          landmark: '',
          city: '',
          state: '',
          pincode: '',
          country: 'India',
          is_default: false
        });
      } else {
        setError(result.error || 'Failed to save address');
      }
    } catch (err) {
      console.error('Error saving address:', err);
      setError('Failed to save address');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm('Delete this address?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/addresses/${addressId}`, {
        method: 'DELETE'
      });
      
      const result = await response.json();
      
      if (result.success) {
        setAddresses(addresses.filter(addr => addr.id !== addressId));
        if (selectedAddress?.id === addressId) {
          setSelectedAddress(null);
        }
      }
    } catch (err) {
      console.error('Error deleting address:', err);
    }
  };

  const calculateOrderSummary = () => {
    const subtotal = getCartTotal() || 0;
    const couponDiscount = appliedCoupon ? parseFloat(appliedCoupon.amount) : 0;
    const shipping = 0; // Free shipping
    const tax = Math.round((subtotal - couponDiscount) * 0.18); // 18% GST after discount
    const total = subtotal - couponDiscount + tax;
    
    return { 
      subtotal, 
      couponDiscount, 
      discount: couponDiscount, // Alias for discount
      shipping, 
      tax, 
      total 
    };
  };
  
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }
    
    try {
      setValidatingCoupon(true);
      setCouponError('');
      
      const response = await fetch(`${API_URL}/api/coupons/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: couponCode.trim().toUpperCase(),
          user_id: user.id,
          cart_total: getCartTotal()
        })
      });
      
      const result = await response.json();
      
      if (result.valid) {
        setAppliedCoupon(result.coupon);
        setCouponCode('');
        setCouponError('');
      } else {
        setCouponError(result.error || 'Invalid coupon code');
        setAppliedCoupon(null);
      }
    } catch (err) {
      console.error('Error validating coupon:', err);
      setCouponError('Failed to validate coupon');
    } finally {
      setValidatingCoupon(false);
    }
  };
  
  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError('');
  };
  
  const handleSelectCoupon = (coupon) => {
    setCouponCode(coupon.code);
    setShowCoupons(false);
    // Auto-apply
    setTimeout(() => handleApplyCoupon(), 100);
  };

  const handleProceedToReview = () => {
    if (!selectedAddress) {
      alert('Please select a delivery address');
      return;
    }
    setStep(2);
  };

  const handleProceedToPayment = async () => {
    try {
      setProcessing(true);
      setStep(3);
      
      const summary = calculateOrderSummary();
      
      // Get authentication token
      const token = session?.access_token;
      if (!token) {
        console.error('❌ CHECKOUT ERROR: No authentication token found');
        console.log('📊 Session state:', session);
        throw new Error('Authentication token not found. Please login again.');
      }
      
      // DEBUG: Log what we're sending
      console.log('📦 CHECKOUT DEBUG - Sending cart:', JSON.stringify(cart, null, 2));
      console.log('💰 Order summary:', summary);
      console.log('🔑 Auth token:', token.substring(0, 20) + '...');
      
      // Create COD order on backend
      const response = await fetch(`${API_URL}/api/payments/create-order`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          amount: summary.total,
          currency: 'INR',
          cart: cart,
          user_id: user.id,
          address_id: selectedAddress.id,
          coupon_code: appliedCoupon?.code || null,
          discount_amount: summary.couponDiscount || 0
        })
      });
      
      const result = await response.json();
      
      console.log('📥 Backend response:', result);
      
      if (!result.success) {
        console.error('❌ CHECKOUT ERROR:', result.error);
        console.error('📋 Full error details:', result);
        throw new Error(result.error || 'Failed to create order');
      }
      
      // Order placed successfully with COD
      console.log('✅ Order placed successfully!', result);
      
      setOrderNumber(result.order_number);
      setOrderId(result.order_id);
      setProcessing(false);
      setStep(4);
      
      // Clear cart after showing success
      await clearCart();
      
    } catch (error) {
      console.error('❌ CHECKOUT ERROR CAUGHT:', error);
      console.log('📋 Error details:', {
        message: error.message,
        stack: error.stack,
        cart,
        user,
        session,
        selectedAddress
      });
      setProcessing(false);
      setStep(2);
      alert('Order placement failed: ' + error.message);
    }
  };

  const summary = calculateOrderSummary();

  // Empty cart check
  if (cart.length === 0) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-7xl mx-auto px-4">
        
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-center gap-4">
            {[
              { num: 1, label: 'Address' },
              { num: 2, label: 'Review' },
              { num: 3, label: 'Payment' }
            ].map((s, i) => (
              <React.Fragment key={s.num}>
                <div className={`flex items-center gap-2 ${step >= s.num ? 'text-orange-600' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                    step >= s.num ? 'bg-orange-600 text-white' : 'bg-gray-200'
                  }`}>
                    {step > s.num ? <CheckCircle className="w-5 h-5" /> : s.num}
                  </div>
                  <span className="font-semibold text-sm hidden sm:inline">{s.label}</span>
                </div>
                {i < 2 && (
                  <div className={`w-12 sm:w-24 h-1 ${step > s.num ? 'bg-orange-600' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-4">
            
            {/* Step 1: Address Selection */}
            {step === 1 && (
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <MapPin className="w-6 h-6 text-orange-600" />
                    Delivery Address
                  </h2>
                  <button
                    onClick={() => setShowAddressForm(!showAddressForm)}
                    className="flex items-center gap-2 px-4 py-2 border-2 border-orange-600 text-orange-600 rounded-lg hover:bg-orange-50 font-semibold text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    Add New
                  </button>
                </div>

                {/* Add Address Form */}
                {showAddressForm && (
                  <form onSubmit={handleAddressSubmit} className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <h3 className="font-bold text-gray-900 mb-4">New Address</h3>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Full Name *</label>
                        <input
                          type="text"
                          required
                          value={addressForm.full_name}
                          onChange={(e) => setAddressForm({...addressForm, full_name: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Phone Number *</label>
                        <input
                          type="tel"
                          required
                          value={addressForm.phone}
                          onChange={(e) => setAddressForm({...addressForm, phone: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />
                      </div>
                      
                      <div className="sm:col-span-2">
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Address Line 1 *</label>
                        <input
                          type="text"
                          required
                          value={addressForm.address_line1}
                          onChange={(e) => setAddressForm({...addressForm, address_line1: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                          placeholder="House No, Building Name"
                        />
                      </div>
                      
                      <div className="sm:col-span-2">
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Address Line 2</label>
                        <input
                          type="text"
                          value={addressForm.address_line2}
                          onChange={(e) => setAddressForm({...addressForm, address_line2: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                          placeholder="Road Name, Area, Colony"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Landmark</label>
                        <input
                          type="text"
                          value={addressForm.landmark}
                          onChange={(e) => setAddressForm({...addressForm, landmark: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">City *</label>
                        <input
                          type="text"
                          required
                          value={addressForm.city}
                          onChange={(e) => setAddressForm({...addressForm, city: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">State *</label>
                        <input
                          type="text"
                          required
                          value={addressForm.state}
                          onChange={(e) => setAddressForm({...addressForm, state: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Pincode *</label>
                        <input
                          type="text"
                          required
                          value={addressForm.pincode}
                          onChange={(e) => setAddressForm({...addressForm, pincode: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Address Type</label>
                        <select
                          value={addressForm.address_type}
                          onChange={(e) => setAddressForm({...addressForm, address_type: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        >
                          <option value="home">Home</option>
                          <option value="work">Work</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      
                      <div className="sm:col-span-2">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={addressForm.is_default}
                            onChange={(e) => setAddressForm({...addressForm, is_default: e.target.checked})}
                            className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500"
                          />
                          <span className="text-sm font-semibold text-gray-700">Set as default address</span>
                        </label>
                      </div>
                    </div>
                    
                    <div className="flex gap-3 mt-4">
                      <button
                        type="submit"
                        disabled={loading}
                        className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-semibold disabled:bg-gray-400"
                      >
                        {loading ? 'Saving...' : 'Save Address'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowAddressForm(false)}
                        className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}

                {/* Address List */}
                {loading && addresses.length === 0 ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-8 h-8 text-orange-600 animate-spin mx-auto" />
                    <p className="text-sm text-gray-500 mt-2">Loading addresses...</p>
                  </div>
                ) : addresses.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <MapPin className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm">No saved addresses. Please add one to continue.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {addresses.map((addr) => (
                      <div
                        key={addr.id}
                        onClick={() => setSelectedAddress(addr)}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedAddress?.id === addr.id
                            ? 'border-orange-600 bg-orange-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs font-bold rounded uppercase">
                                {addr.address_type}
                              </span>
                              {addr.is_default && (
                                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-bold rounded">
                                  DEFAULT
                                </span>
                              )}
                              {selectedAddress?.id === addr.id && (
                                <CheckCircle className="w-4 h-4 text-orange-600" />
                              )}
                            </div>
                            <p className="font-bold text-gray-900">{addr.full_name}</p>
                            <p className="text-sm text-gray-600">{addr.phone}</p>
                            <p className="text-sm text-gray-600 mt-1">
                              {addr.address_line1}, {addr.address_line2 && `${addr.address_line2}, `}
                              {addr.landmark && `${addr.landmark}, `}
                              {addr.city}, {addr.state} - {addr.pincode}
                            </p>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteAddress(addr.id);
                            }}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Continue Button */}
                <button
                  onClick={handleProceedToReview}
                  disabled={!selectedAddress}
                  className="w-full mt-6 py-3 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-300 text-white font-bold rounded-lg transition-colors disabled:cursor-not-allowed"
                >
                  Continue to Review Order
                </button>
              </div>
            )}

            {/* Step 2: Order Review */}
            {step === 2 && (
              <div className="space-y-4">
                {/* Delivery Address */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-gray-900 flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-orange-600" />
                      Delivery Address
                    </h3>
                    <button
                      onClick={() => setStep(1)}
                      className="text-orange-600 hover:text-orange-700 text-sm font-semibold flex items-center gap-1"
                    >
                      <Edit2 className="w-4 h-4" />
                      Change
                    </button>
                  </div>
                  {selectedAddress && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="font-bold text-gray-900">{selectedAddress.full_name}</p>
                      <p className="text-sm text-gray-600">{selectedAddress.phone}</p>
                      <p className="text-sm text-gray-600 mt-1">
                        {selectedAddress.address_line1}, {selectedAddress.address_line2 && `${selectedAddress.address_line2}, `}
                        {selectedAddress.landmark && `${selectedAddress.landmark}, `}
                        {selectedAddress.city}, {selectedAddress.state} - {selectedAddress.pincode}
                      </p>
                    </div>
                  )}
                </div>

                {/* Order Items */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Package className="w-5 h-5 text-orange-600" />
                    Order Items ({cart.length})
                  </h3>
                  <div className="space-y-3">
                    {cart.map((item) => (
                      <div key={item.cartItemId || item.id} className="flex gap-4 pb-3 border-b border-gray-100 last:border-0">
                        <div className="w-16 h-16 bg-gray-50 rounded-lg flex items-center justify-center overflow-hidden shrink-0">
                          {(item.variant?.image_url || item.image_url) ? (
                            <img src={item.variant?.image_url || item.image_url} alt={item.name} className="w-full h-full object-contain" />
                          ) : (
                            <Package className="w-8 h-8 text-gray-300" />
                          )}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900 text-sm">{item.name}</h4>
                          {item.variant_name && (
                            <p className="text-xs text-blue-600">{item.variant_name}</p>
                          )}
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-xs text-gray-500">Qty: {item.quantity}</span>
                            <span className="font-bold text-gray-900">₹{((item.price || 0) * (item.quantity || 0)).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={() => setStep(1)}
                    className="flex-1 py-3 border-2 border-gray-300 text-gray-700 font-bold rounded-lg hover:bg-gray-50"
                  >
                    <ArrowLeft className="w-5 h-5 inline mr-2" />
                    Back
                  </button>
                  <button
                    onClick={handleProceedToPayment}
                    className="flex-1 py-3 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-lg"
                  >
                    Place Order (Cash on Delivery)
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Processing Order */}
            {step === 3 && (
              <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200 text-center">
                <Loader2 className="w-16 h-16 text-orange-600 animate-spin mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Processing Your Order</h2>
                <p className="text-gray-600 mb-2">Please wait while we confirm your order...</p>
                <p className="text-sm text-gray-500">Payment Method: Cash on Delivery</p>
              </div>
            )}

            {/* Step 4: Order Success */}
            {step === 4 && (
              <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Order Placed Successfully!</h2>
                <p className="text-gray-600 mb-4">Your order has been confirmed</p>
                
                <div className="bg-orange-50 rounded-lg p-4 mb-6">
                  <p className="text-sm text-gray-600 mb-1">Order Number</p>
                  <p className="text-2xl font-mono font-bold text-orange-600">{orderNumber}</p>
                </div>
                
                <div className="bg-blue-50 rounded-lg p-4 mb-6">
                  <p className="text-sm font-semibold text-blue-900 mb-2">Payment Method: Cash on Delivery</p>
                  <p className="text-sm text-blue-700">Pay ₹{summary?.total.toLocaleString()} when your order is delivered</p>
                </div>
                
                <div className="space-y-3">
                  <button
                    onClick={() => navigate('/orders')}
                    className="w-full bg-orange-600 text-white py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors"
                  >
                    View Order Details
                  </button>
                  <button
                    onClick={() => navigate('/')}
                    className="w-full border border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                  >
                    Continue Shopping
                  </button>
                </div>
              </div>
            )}

          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 sticky top-6">
              <h3 className="font-bold text-gray-900 mb-4 uppercase tracking-wide">Order Summary</h3>
              
              <div className="space-y-3 mb-4 pb-4 border-b border-gray-200">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal ({cart.reduce((sum, item) => sum + item.quantity, 0)} items)</span>
                  <span className="font-semibold text-gray-900">₹{(summary?.subtotal || 0).toLocaleString()}</span>
                </div>
                
                {/* Coupon Discount */}
                {summary?.couponDiscount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span className="flex items-center gap-1">
                      Coupon Discount
                      {appliedCoupon && (
                        <span className="text-xs font-mono bg-green-100 px-2 py-0.5 rounded">
                          {appliedCoupon.code}
                        </span>
                      )}
                    </span>
                    <span className="font-semibold">- ₹{(summary?.couponDiscount || 0).toLocaleString()}</span>
                  </div>
                )}
                
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Shipping</span>
                  <span className="font-semibold text-green-600">FREE</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">GST (18%)</span>
                  <span className="font-semibold text-gray-900">₹{(summary?.tax || 0).toLocaleString()}</span>
                </div>
              </div>
              
              {/* Coupon Input Section */}
              {step < 3 && (
                <div className="mb-4 pb-4 border-b border-gray-200">
                  {!appliedCoupon ? (
                    <>
                      <div className="flex gap-2 mb-2">
                        <input
                          type="text"
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                          placeholder="Enter coupon code"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          disabled={validatingCoupon}
                        />
                        <button
                          onClick={handleApplyCoupon}
                          disabled={validatingCoupon || !couponCode.trim()}
                          className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {validatingCoupon ? 'Checking...' : 'Apply'}
                        </button>
                      </div>
                      
                      {couponError && (
                        <p className="text-xs text-red-600 mt-1">{couponError}</p>
                      )}
                      
                      {availableCoupons.length > 0 && (
                        <button
                          onClick={() => setShowCoupons(!showCoupons)}
                          className="text-xs text-orange-600 hover:text-orange-700 font-semibold mt-2"
                        >
                          {showCoupons ? 'Hide' : 'View'} available coupons ({availableCoupons.length})
                        </button>
                      )}
                      
                      {showCoupons && availableCoupons.length > 0 && (
                        <div className="mt-3 space-y-2 max-h-40 overflow-y-auto">
                          {availableCoupons.map((coupon) => (
                            <div
                              key={coupon.id}
                              onClick={() => handleSelectCoupon(coupon)}
                              className="p-2 border border-gray-200 rounded-lg hover:border-orange-500 hover:bg-orange-50 cursor-pointer transition-colors"
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-xs font-mono font-bold text-gray-900">{coupon.code}</p>
                                  <p className="text-xs text-gray-600">{coupon.name}</p>
                                </div>
                                <span className="text-xs font-bold text-green-600">₹{coupon.amount}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-semibold text-green-900">Coupon Applied!</span>
                        <button
                          onClick={handleRemoveCoupon}
                          className="text-xs text-red-600 hover:text-red-700 font-semibold"
                        >
                          Remove
                        </button>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-mono font-bold text-green-800">{appliedCoupon.code}</span>
                        <span className="text-sm font-bold text-green-600">- ₹{appliedCoupon.amount}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              <div className="flex justify-between text-lg font-bold text-gray-900 mb-6">
                <span>Total</span>
                <span>₹{(summary?.total || 0).toLocaleString()}</span>
              </div>
              
              {summary?.discount > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                  <p className="text-sm text-green-700 font-semibold">
                    You're saving ₹{(summary.discount || 0).toLocaleString()}!
                  </p>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
